/**
 * Calcula value/currency a partir de items resolvidos.
 * Não conhece DOM, detectors, Transport nem Dispatcher.
 * Não muta o input.
 */

export type ResolvedCommerceItem = {
  productId?: string;
  sku?: string;
  productName?: string;
  price?: number;
  quantity?: number;
  currency?: string;
  category?: string;
  variant?: string;
  brand?: string;
  type?: string;
  metadata?: Record<string, unknown>;
};

export type OrderCalculationInput = {
  items?: ResolvedCommerceItem[];
  value?: number;
  currency?: string;
  defaultCurrency?: string;
};

export type OrderCalculationResult = {
  items: ResolvedCommerceItem[];
  value?: number;
  currency?: string;
};

export class OrderCalculator {
  calculate(input: OrderCalculationInput): OrderCalculationResult {
    const sourceItems = Array.isArray(input.items) ? input.items : [];
    const items = sourceItems.map(item => this.cloneItem(item));

    for (const item of items) {
      if (item.quantity == null || !Number.isFinite(item.quantity)) {
        item.quantity = 1;
      }
    }

    const value =
      typeof input.value === 'number' && Number.isFinite(input.value)
        ? input.value
        : this.sumValue(items);

    const currency = this.resolveCurrency(input, items);

    return {
      items,
      ...(value !== undefined ? { value } : {}),
      ...(currency !== undefined ? { currency } : {}),
    };
  }

  private sumValue(items: ResolvedCommerceItem[]): number | undefined {
    let total = 0;
    let hasPriced = false;

    for (const item of items) {
      if (typeof item.price !== 'number' || !Number.isFinite(item.price)) {
        continue;
      }
      const qty =
        typeof item.quantity === 'number' && Number.isFinite(item.quantity)
          ? item.quantity
          : 1;
      total += item.price * qty;
      hasPriced = true;
    }

    return hasPriced ? total : undefined;
  }

  /**
   * Ordem: currency manual → primeiro item com currency → defaultCurrency.
   * Multi-currency: determinístico — usa o primeiro item com currency
   * (não normaliza conversão entre moedas nesta etapa).
   */
  private resolveCurrency(
    input: OrderCalculationInput,
    items: ResolvedCommerceItem[]
  ): string | undefined {
    if (typeof input.currency === 'string' && input.currency.trim()) {
      return input.currency.trim();
    }

    for (const item of items) {
      if (typeof item.currency === 'string' && item.currency.trim()) {
        return item.currency.trim();
      }
    }

    if (
      typeof input.defaultCurrency === 'string' &&
      input.defaultCurrency.trim()
    ) {
      return input.defaultCurrency.trim();
    }

    return undefined;
  }

  private cloneItem(item: ResolvedCommerceItem): ResolvedCommerceItem {
    return {
      ...item,
      ...(item.metadata ? { metadata: { ...item.metadata } } : {}),
    };
  }
}
